import React, { useState } from 'react';
import PlayerTable from './PlayerTable';
import DraftBoard from './DraftBoard';
import useLeagueViewState, { statOptions, validStat } from '../useLeagueViewState';

const selectClass = 'px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-nba-orange focus:border-transparent';
const checkboxClass = 'h-4 w-4 text-nba-orange focus:ring-nba-orange border-gray-600 rounded bg-gray-700';

// A player matches a position chip when one of his positions reaches it: NBA's generic G covers
// PG and SG, F covers SF and PF (the same rule as the league's slots, leagues.POSITION_REACH).
const REACH = { PG: ['PG', 'G'], SG: ['SG', 'G'], G: ['PG', 'SG', 'G'], SF: ['SF', 'F'], PF: ['PF', 'F'], F: ['SF', 'PF', 'F'], C: ['C'] };
const playsPosition = (player, chip) => (player.positions?.length ? player.positions : [player.position])
  .some(pos => (REACH[pos] || [pos]).includes(chip));

// The Player Rankings tab: toolbar (stats, search, positions, star premium, filters) and the table.
// Everything picked here is remembered per league (useLeagueViewState).
const PlayerRankings = ({ players, fantasyTeams, config, draftMode, draftPlan, onDraftPlayer, onUndraftPlayer }) => {
  const caps = config.capabilities || {};
  const categoryKeys = (config.categories || []).map(c => c.key);
  // Leagues with positions filter by their slot positions; all-flex leagues by NBA's G/F/C.
  const chips = caps.positions?.length ? caps.positions : ['G', 'F', 'C'];
  const [view, setView] = useLeagueViewState(config.league?.id, 'players', {
    statType: config.default_stat_type || 'projected', positions: chips, available: true, healthy: true,
    priceExponent: null, punts: [], tonight: false, tomorrow: false, opp: false, likely: false,
  }, (picked) => ({
    ...validStat(config)(picked),
    ...(picked.punts && { punts: picked.punts.filter(c => categoryKeys.includes(c)) }),
    ...(picked.positions && { positions: picked.positions.filter(p => chips.includes(p)) }),
  }));
  const [searchTerm, setSearchTerm] = useState('');
  // Phones: only search shows; the other levers fold into Filters (the badge counts the ones changed).
  const [filtersOpen, setFiltersOpen] = useState(false);
  const leagueExponent = config.league?.settings?.draft?.price_exponent ?? 1;
  const showPositionFilter = !draftMode || Boolean(caps.positions?.length);

  // The NBA's day is an Eastern date; label the filters with it so it is clear which night they mean.
  const dayLabel = (offset) => new Date(Date.now() + offset * 864e5).toLocaleDateString('en-US',
    { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/New_York' });
  const search = searchTerm.toLowerCase();
  const rotationOn = players.some(p => p.rotation);   // the two rotation filters only apply while some lineup is posted
  const filtered = players.filter(player => {
    const mine = player.fantasy_team?.abbreviation === config.MY_TEAM_ABV;
    return (player.name.toLowerCase().includes(search) || player.team.toLowerCase().includes(search))
      && (mine || !view.available || !player.drafted)
      && (!showPositionFilter || view.positions.some(chip => playsPosition(player, chip)))
      && (!view.healthy || !player.is_injured)
      && (!view.tonight || player.plays_today)
      && (!view.tomorrow || player.plays_tomorrow)
      && (!rotationOn || !view.opp || (player.rotation && !player.rotation.inactive && player.rotation.opp >= 2))
      && (!rotationOn || !view.likely || (player.rotation && player.rotation.p >= 0.8));
  });
  const changed = [view.statType !== (config.default_stat_type || 'projected'), showPositionFilter && view.positions.length !== chips.length,
    caps.auction && view.priceExponent != null, !view.available, !view.healthy, view.tonight, view.tomorrow, rotationOn && view.opp, rotationOn && view.likely].filter(Boolean).length;
  const lever = filtersOpen ? 'flex' : 'hidden sm:flex';
  const togglePosition = (chip) => setView(v => ({
    positions: v.positions.includes(chip) ? v.positions.filter(p => p !== chip) : [...v.positions, chip],
  }));

  return (
    // In-season: the search box takes the 14rem slot Daily Leaders gives its date stepper, and the toolbar sits bare above the table card at the same offsets as Daily Leaders, so the
    // two tabs line up. Draft mode keeps its single card.
    <div className={draftMode ? 'bg-gray-800 rounded-lg shadow-xl' : 'py-2 sm:px-2 sm:py-1'}>
      <div className={draftMode ? 'px-2 sm:px-6 py-2 sm:py-4 border-b border-gray-700 overflow-x-hidden' : 'mb-2 sm:mb-1.5 sm:min-h-[60px] flex flex-col justify-center overflow-x-hidden'}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 sm:gap-4 overflow-x-hidden">
          <div className={`${lever} items-center space-x-2`}>
            <span className="text-sm text-gray-300">Stats:</span>
            <select value={view.statType} onChange={(e) => setView({ statType: e.target.value })} className={selectClass}>
              {statOptions(config).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>

          <div className="flex gap-2 flex-1 order-first sm:flex-none sm:w-56">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search players or teams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-1.5 sm:py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-nba-orange focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button onClick={() => setFiltersOpen(!filtersOpen)} aria-expanded={filtersOpen}
                    className={`sm:hidden flex items-center gap-1.5 px-3 rounded-md border text-sm ${filtersOpen ? 'bg-gray-600 border-gray-500 text-white' : 'bg-gray-700 border-gray-600 text-gray-200'}`}>
              Filters
              {changed > 0 && <span className="min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-nba-orange text-onaccent text-[10px] font-bold flex items-center justify-center">{changed}</span>}
            </button>
          </div>

          {showPositionFilter && (
            <div className={`${lever} items-center space-x-4`}>
              <span className="text-sm text-gray-300">Position:</span>
              {chips.map(chip => (
                <label key={chip} className="flex items-center">
                  <input type="checkbox" checked={view.positions.includes(chip)} onChange={() => togglePosition(chip)} className={checkboxClass} />
                  <span className="ml-1 text-sm text-gray-300">{chip}</span>
                </label>
              ))}
            </div>
          )}

          {caps.auction && draftMode && (
            <div
              className={`${lever} items-center space-x-2`}
              title={`Auction $ follow each player's value above replacement, raised to this power. 1 splits money in proportion to value; higher pays stars more. This league's default: ${leagueExponent}`}
            >
              <span className="text-sm text-gray-300">Star premium:</span>
              <input
                type="range" min="0.5" max="2" step="0.05"
                value={view.priceExponent ?? leagueExponent}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  setView({ priceExponent: Math.abs(value - leagueExponent) < 1e-9 ? null : value });
                }}
                className="w-20 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <span className="text-sm text-gray-300 w-10 text-center">{(view.priceExponent ?? leagueExponent).toFixed(2)}</span>
            </div>
          )}

          <div className={`${lever} items-center flex-wrap gap-x-4 gap-y-1`}>
            <label className="flex items-center">
              <input type="checkbox" checked={view.available} onChange={(e) => setView({ available: e.target.checked })} className={checkboxClass} />
              <span className="ml-2 text-sm text-gray-300">Available</span>
            </label>
            <label className="flex items-center" title="Only players whose team plays on this Eastern date">
              <input type="checkbox" checked={view.tonight} onChange={(e) => setView({ tonight: e.target.checked })} className={checkboxClass} />
              <span className="ml-2 text-sm text-gray-300">Tonight ({dayLabel(0)})</span>
            </label>
            <label className="flex items-center" title="Only players whose team plays on this Eastern date">
              <input type="checkbox" checked={view.tomorrow} onChange={(e) => setView({ tomorrow: e.target.checked })} className={checkboxClass} />
              <span className="ml-2 text-sm text-gray-300">Tomorrow ({dayLabel(1)})</span>
            </label>
            {rotationOn && <>
              <label className="flex items-center" title="Teammates who are out give him at least 2 extra minutes tonight (NBA lineup sheet; only teams with a posted lineup)">
                <input type="checkbox" checked={view.opp} onChange={(e) => setView({ opp: e.target.checked })} className={checkboxClass} />
                <span className="ml-2 text-sm text-gray-300">Opp +2 min</span>
              </label>
              <label className="flex items-center" title="At least an 80% chance of playing tonight (NBA lineup sheet; only teams with a posted lineup)">
                <input type="checkbox" checked={view.likely} onChange={(e) => setView({ likely: e.target.checked })} className={checkboxClass} />
                <span className="ml-2 text-sm text-gray-300">Likely to play</span>
              </label>
            </>}
            <label className="flex items-center">
              <input type="checkbox" checked={view.healthy} onChange={(e) => setView({ healthy: e.target.checked })} className={checkboxClass} />
              <span className="ml-2 text-sm text-gray-300">Healthy</span>
            </label>
          </div>
        </div>
      </div>

      {React.createElement(draftMode ? DraftBoard : PlayerTable, {
        players: filtered,
        fantasyTeams,
        onDraftPlayer,
        onUndraftPlayer,
        priceExponent: view.priceExponent,
        config,
        statType: view.statType,
        punts: view.punts,
        onPuntsChange: (punts) => setView({ punts }),
        ...(draftMode && { draftPlan, allPlayers: players }),
      })}
    </div>
  );
};

export default PlayerRankings;
